import React from 'react';
import { generatePath, useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';

import type { AiOriginalImage } from 'src/shared/graphql/generated';
import { usePublicOrgInfoQuery } from 'src/shared/graphql/generated';
import { useIsEmbed } from 'src/shared/hooks';
import { Route } from 'src/shared/route';

import { useHomeownerAppContext } from '../../hooks';

import {
  UploadImage,
  alwaysCreateNewProject,
  closeUploadSheetAfterUpload,
  uploadToProjectWithoutSelection,
} from '.';

const UploadHomeownerImage = () => {
  const history = useHistory<{ from?: string }>();
  const location = useLocation();
  const isEmbed = useIsEmbed();
  const searchParams = new URLSearchParams(location.search);
  const affOrgId = searchParams.get('aff_org_id');

  const { user, setImageFileToUpload, imageFileToUpload } =
    useHomeownerAppContext();

  const handleImageStatusDone = ({ image }: { image: AiOriginalImage }) => {
    alwaysCreateNewProject(false);
    uploadToProjectWithoutSelection(false);
    if (closeUploadSheetAfterUpload()) {
      return;
    }

    // customize-prototype redirect
    if (
      history.location.pathname === Route.HOMEOWNER_APP_CUSTOMIZE_UPLOAD.path
    ) {
      const displayImageId = image.aiDisplayImages?.[0]?.id;

      if (displayImageId) {
        history.push({
          pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE.path, {
            aiDisplayImageId: displayImageId,
          }),
          state: undefined,
        });
      }
    } else if (!isEmbed && image.projects?.length) {
      const projectId = image.projects[0].id;
      history.push({
        pathname: generatePath(Route.HOMEOWNER_APP_PROJECT.path, {
          projectId,
        }),
        state: undefined,
      });
    } else {
      history.push(
        {
          pathname: generatePath(Route.HOMEOWNER_APP_ORIGINAL.path, {
            aiOriginalImageId: image.id,
          }),
          state: undefined,
        },
        { from: 'upload' },
      );
    }
  };

  const { data } = usePublicOrgInfoQuery({
    variables: {
      publicIdentifier: affOrgId || '',
    },
    skip: !affOrgId,
  });

  const { displayName, id } = data?.publicOrgInfo || {};

  const orgId = id || user?.orgs?.[0]?.id;

  return (
    <UploadImage
      email={user?.email}
      handleImageStatusDone={handleImageStatusDone}
      imageFileToUpload={imageFileToUpload}
      orgDisplayName={displayName}
      orgId={orgId}
      setImageFileToUpload={setImageFileToUpload}
    />
  );
};

export { UploadHomeownerImage };
